import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { authAPI } from '../services/api';
import { PLATFORM_NAME } from '../../../config/brand';
import { nextFromSearch, loginPathWithNext } from '../../../utils/authRedirect';
import '../../../styles/Auth.css';

const OTP_LENGTH = 6;

const YEAR_OPTIONS = [
  '1st Year',
  '2nd Year',
  '3rd Year',
  '4th Year',
  'Other',
];

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    mobile: '',
    college: '',
    year: '',
    batch_id: '',
  });
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(true);
  const [batchLocked, setBatchLocked] = useState(false);
  const [inviteBatchName, setInviteBatchName] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const [otpError, setOtpError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const otpRefs = useRef([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [cooldown, setCooldown] = useState(20);
  const timerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const inviteCode = (searchParams.get('batch') || '').trim();
  const postAuthNext = nextFromSearch(location.search);

  const { register } = useAuth();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBatchesLoading(true);
      setInviteError('');
      try {
        if (inviteCode) {
          const res = await authAPI.getBatchByInvite(inviteCode);
          const batch = res.data?.batch;
          if (!cancelled && batch?.id) {
            setBatches([{ id: batch.id, name: batch.name }]);
            setFormData((prev) => ({ ...prev, batch_id: String(batch.id) }));
            setBatchLocked(true);
            setInviteBatchName(batch.name || '');
          }
        } else {
          const res = await authAPI.listBatches();
          if (!cancelled) {
            setBatches(res.data?.items || []);
            setBatchLocked(false);
            setInviteBatchName('');
          }
        }
      } catch {
        if (!cancelled) {
          if (inviteCode) {
            setInviteError('This registration link is invalid. Ask your instructor for a new link.');
            setBatches([]);
            setBatchLocked(false);
          } else {
            setBatches([]);
          }
        }
      } finally {
        if (!cancelled) setBatchesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [inviteCode]);

  const startCooldown = useCallback(() => {
    setCooldown(20);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const validateForm = () => {
    if (formData.name.trim().length === 0) {
      setError('Please enter your full name');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    if (!/[A-Za-z]/.test(formData.password) || !/[0-9]/.test(formData.password)) {
      setError('Password must contain at least one letter and one number');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    const mobileDigits = formData.mobile.replace(/[\s\-+()]/g, '');
    if (!mobileDigits) {
      setError('Please enter your phone number');
      return false;
    }
    if (!/^\d{10,15}$/.test(mobileDigits)) {
      setError('Phone number must be 10–15 digits');
      return false;
    }
    if (!formData.college.trim()) {
      setError('Please enter your college');
      return false;
    }
    if (!formData.year) {
      setError('Please select your year');
      return false;
    }
    if (!formData.batch_id) {
      setError('Please select your batch');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setError('');

    const result = await register({
      name: formData.name,
      email: formData.email,
      password: formData.password,
      mobile: formData.mobile.trim(),
      college: formData.college.trim(),
      year: formData.year,
      batch_id: Number(formData.batch_id),
    });

    if (result.success && result.needsVerification) {
      setRegistrationSuccess(true);
      setRegisteredEmail(result.email || formData.email);
      startCooldown();
    } else if (!result.success) {
      setError(result.error);
    }

    setIsLoading(false);
  };

  const handleResendVerification = async () => {
    setResending(true);
    setResendMessage('');
    try {
      await authAPI.resendVerification(registeredEmail);
      setResendMessage('A new code has been sent to your email.');
      setOtp(Array(OTP_LENGTH).fill(''));
      startCooldown();
    } catch {
      setResendMessage('Failed to resend. Please try again.');
    }
    setResending(false);
  };

  // OTP handlers
  const handleOtpChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setOtpError('');
    if (value && index < OTP_LENGTH - 1) otpRefs.current[index + 1]?.focus();
    if (value && index === OTP_LENGTH - 1 && newOtp.every(d => d !== '')) {
      handleVerifyOtp(newOtp.join(''));
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) otpRefs.current[index - 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted.length) return;
    const newOtp = Array(OTP_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) newOtp[i] = pasted[i];
    setOtp(newOtp);
    otpRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
    if (pasted.length === OTP_LENGTH) handleVerifyOtp(pasted);
  };

  const handleVerifyOtp = async (code) => {
    const otpCode = code || otp.join('');
    if (otpCode.length !== OTP_LENGTH) {
      setOtpError('Please enter the complete 6-digit code.');
      return;
    }
    setVerifying(true);
    setOtpError('');
    try {
      await authAPI.verifyEmail(registeredEmail, otpCode);
      navigate(loginPathWithNext(postAuthNext), { state: { verified: true } });
    } catch (err) {
      setOtpError(err.response?.data?.detail || 'Invalid or expired code. Please try again.');
      setOtp(Array(OTP_LENGTH).fill(''));
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    }
    setVerifying(false);
  };

  useEffect(() => {
    if (registrationSuccess && otpRefs.current[0]) {
      setTimeout(() => otpRefs.current[0]?.focus(), 200);
    }
  }, [registrationSuccess]);

  // ── OTP verification screen after registration ─────────────────
  if (registrationSuccess) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="verification-icon">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="32" r="32" fill="#667eea" opacity="0.1"/>
                <rect x="16" y="22" width="32" height="22" rx="3" stroke="#667eea" strokeWidth="2.5" fill="none"/>
                <path d="M16 25l16 12 16-12" stroke="#667eea" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            </div>
            <h1>Verify Your Email</h1>
            <p>We sent a 6-digit code to</p>
            <p style={{ fontWeight: 600, color: '#2d3748', fontSize: '16px', marginTop: '4px' }}>
              {registeredEmail}
            </p>
          </div>

          <div className="otp-input-group" onPaste={handleOtpPaste}>
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={el => otpRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                className="otp-input"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                autoComplete="one-time-code"
                disabled={verifying}
              />
            ))}
          </div>

          {otpError && <p className="otp-error-message">{otpError}</p>}

          <button
            className="auth-button"
            onClick={() => handleVerifyOtp()}
            disabled={verifying || otp.some(d => d === '')}
            style={{ marginTop: '16px' }}
          >
            {verifying ? (
              <><span className="spinner"></span>Verifying…</>
            ) : (
              'Verify Email'
            )}
          </button>

          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <p style={{ color: '#a0aec0', fontSize: '13px', margin: '0 0 12px' }}>
              Didn't receive the code? Check your spam folder.
            </p>
            <button
              className="auth-button auth-button-outline"
              onClick={handleResendVerification}
              disabled={resending || cooldown > 0}
              style={{ padding: '8px 24px', fontSize: '13px', minHeight: 'auto' }}
            >
              {resending ? (
                <><span className="spinner"></span>Sending…</>
              ) : cooldown > 0 ? (
                `Resend in ${cooldown}s`
              ) : (
                'Resend Code'
              )}
            </button>
            {resendMessage && (
              <p style={{ color: '#38a169', fontSize: '14px', marginTop: '8px' }}>{resendMessage}</p>
            )}
          </div>

          <div className="auth-footer" style={{ marginTop: '16px' }}>
            <p style={{ color: '#94a3b8', fontSize: '12px' }}>Code expires in 10 minutes</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Registration form ──────────────────────────────────────────
  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <img src="/newlogo.png" alt={PLATFORM_NAME} className="auth-brand-logo" />
          <h1>Create Account</h1>
          <p>Create an account to take your assessments</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-message">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm1 15H9v-2h2v2zm0-4H9V5h2v6z" fill="currentColor"/>
              </svg>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              type="text" id="name" name="name"
              value={formData.name} onChange={handleChange}
              placeholder="Enter your full name" required autoComplete="name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email" id="email" name="email"
              value={formData.email} onChange={handleChange}
              placeholder="Enter your email" required autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="mobile">Phone Number</label>
            <input
              type="tel" id="mobile" name="mobile"
              value={formData.mobile} onChange={handleChange}
              placeholder="e.g. +91 9876543210" autoComplete="tel" maxLength={20}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="college">College</label>
            <input
              type="text" id="college" name="college"
              value={formData.college} onChange={handleChange}
              placeholder="Enter your college name" required autoComplete="organization"
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label htmlFor="year">Year</label>
            <select
              id="year"
              name="year"
              value={formData.year}
              onChange={handleChange}
              required
            >
              <option value="">Select your year</option>
              {YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="batch_id">Batch</label>
            {batchLocked ? (
              <input
                type="text"
                id="batch_id"
                value={inviteBatchName || 'Assigned batch'}
                readOnly
                disabled
              />
            ) : (
              <select
                id="batch_id"
                name="batch_id"
                value={formData.batch_id}
                onChange={handleChange}
                required
                disabled={batchesLoading}
              >
                <option value="">
                  {batchesLoading ? 'Loading batches…' : 'Select your batch'}
                </option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            )}
            {batchLocked && !inviteError && (
              <p style={{ margin: '8px 0 0', fontSize: 13, color: '#64748b' }}>
                Batch preselected from your registration link.
              </p>
            )}
            {inviteError && (
              <p style={{ margin: '8px 0 0', fontSize: 13, color: '#b45309' }}>
                {inviteError}
              </p>
            )}
            {!batchesLoading && !inviteCode && batches.length === 0 && (
              <p style={{ margin: '8px 0 0', fontSize: 13, color: '#b45309' }}>
                No batches are available yet. Ask your instructor to create one.
              </p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password" id="password" name="password"
              value={formData.password} onChange={handleChange}
              placeholder="Min 6 characters, 1 letter + 1 number"
              required autoComplete="new-password" minLength="6"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password" id="confirmPassword" name="confirmPassword"
              value={formData.confirmPassword} onChange={handleChange}
              placeholder="Confirm your password"
              required autoComplete="new-password"
            />
          </div>

          <button type="submit" className="auth-button" disabled={isLoading || batchesLoading || batches.length === 0 || Boolean(inviteError)}>
            {isLoading ? (
              <><span className="spinner"></span>Creating account…</>
            ) : (
              'Register'
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <Link to={loginPathWithNext(postAuthNext)}>Login here</Link>
          </p>
          <p style={{ marginTop: '12px' }}>
            <a href="/" className="back-to-landing">← Back to Home</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
